import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC } from "react";

import { CommonColors } from "../../../types/colors";
import { Cta } from "../../buttons/cta";
import { AppendixCard } from "../../cards/appendix-card";
import { ContactCard } from "../../cards/contact-card";
import { FlatCard } from "../../cards/flat-card";
import { HorizontalCard } from "../../cards/horizontal-card";
import { ImageCard } from "../../cards/image-card";
import { MaterialCard } from "../../cards/material-card";
import { PortraitCard } from "../../cards/portrait-card";
import {
  backgroundClasses,
  gridColumnSpacingsClasses,
  gridRowSpacingsClasses,
  gridTemplateColumnClasses,
  paddingClasses,
} from "./styles";
import {
  GridColorSchemes,
  GridDensity,
  GridSpacing,
  GridTitleAlignments,
  GridTitleColors,
  GridTitleSizes,
} from "./types";

function getTitleColor(titleColor: GridTitleColors) {
  switch (titleColor) {
    case GridTitleColors.NEUTRAL:
      return "text-black";
    case GridTitleColors.TEXT_LIGHT:
      return "text-light";
    case GridTitleColors.PRIMARY:
    default:
      return "text-primary";
  }
}

export interface GridProps extends Queries.GridDataFragment {}

export const Grid: FC<GridProps> = ({
  title,
  titleAlignment,
  titleSize,
  items,
  backgroundImage,
  _padding,
  itemsDensity: _itemsDensity,
  colorScheme,
}) => {
  // There is a dedicated value in GridSpacing for explicitly setting 0 spacing ("none")
  const padding = (_padding as GridSpacing) ?? GridSpacing.MD;
  const itemDensity = (_itemsDensity as GridDensity) ?? GridDensity.COMPACT;

  const titleColor: GridTitleColors = (() => {
    switch (colorScheme as GridColorSchemes) {
      case GridColorSchemes.Primary:
      case GridColorSchemes.Dark:
        return GridTitleColors.TEXT_LIGHT;
      case GridColorSchemes.LightPrimaryTitle:
      case GridColorSchemes.NeutralPrimaryTitle:
        return GridTitleColors.PRIMARY;
      default: // includes all material cases and [Neutral|Light]DarkTitle
        return GridTitleColors.NEUTRAL;
    }
  })();

  const backgroundColor: CommonColors = (() => {
    switch (colorScheme) {
      case GridColorSchemes.LightDarkTitle:
      case GridColorSchemes.LightPrimaryTitle:
        return CommonColors.Light;
      case GridColorSchemes.NeutralDarkTitle:
      case GridColorSchemes.NeutralPrimaryTitle:
        return CommonColors.Neutral;
      default: // includes all material cases, Primary and Dark
        return colorScheme as CommonColors;
    }
  })();

  const columnsSpacing: GridSpacing = (() => {
    switch (itemDensity) {
      case GridDensity.SINGLE_ELEMENT:
        return GridSpacing.None;
      case GridDensity.COMPACT:
        return GridSpacing.SM;
      case GridDensity.REGULAR:
        return GridSpacing.MD;
      case GridDensity.LARGE:
        return GridSpacing.LG;
      default:
        return GridSpacing.MD;
    }
  })();

  const rowsSpacing: GridSpacing = (() => {
    switch (itemDensity) {
      case GridDensity.SINGLE_ELEMENT:
        return GridSpacing.SM;
      case GridDensity.COMPACT:
        return GridSpacing.MD;
      case GridDensity.REGULAR:
        return GridSpacing.LG;
      case GridDensity.LARGE:
        return GridSpacing.XL;
      default:
        return GridSpacing.MD;
    }
  })();

  return (
    <div
      className={clsx(
        paddingClasses[padding],
        backgroundColor && backgroundClasses[backgroundColor as CommonColors],
        "px-8 lg:px-20"
      )}
    >
      {title && (
        <h2
          className={clsx("w-full", [
            getTitleColor(titleColor),
            titleSize === GridTitleSizes.SM ? "text-xl mb-4" : "text-3xl lg:text-4xl mb-5 lg:mb-12",
            titleAlignment === GridTitleAlignments.CENTER ? "text-center" : "text-left",
          ])}
        >
          {title}
        </h2>
      )}
      <div
        className={clsx(
          "relative grid grid-cols-1",
          gridColumnSpacingsClasses[columnsSpacing],
          gridRowSpacingsClasses[rowsSpacing],
          gridTemplateColumnClasses[itemDensity]
        )}
      >
        {items?.map((item, i) => {
          switch (item?.__typename) {
            case "ContentfulAppendixCard":
              return (
                <AppendixCard key={i} light={backgroundColor === CommonColors.Neutral} {...item} />
              );
            case "ContentfulFlatCard":
              return <FlatCard {...item} key={i} />;
            case "ContentfulImageCard":
              return <ImageCard {...item} key={i} />;
            case "ContentfulMaterialCard":
              return <MaterialCard {...item} key={i} />;
            case "ContentfulPortraitCard":
              return <PortraitCard {...item} key={i} className="w-full h-full" />;
            case "ContentfulContactCard":
              return <ContactCard {...item} key={i} className="w-full h-full" />;
            case "ContentfulCta":
              return (
                <Cta
                  {...item}
                  key={i}
                  className="w-full"
                  buttonClassName="w-full !whitespace-normal"
                  variant="neutral-light"
                  size="lg"
                />
              );
            case "ContentfulHorizontalCard":
              return <HorizontalCard {...item} key={i} className="w-full" />;
            default:
              return null;
          }
        })}
        {backgroundImage?.gatsbyImageData && (
          <GatsbyImage
            image={backgroundImage.gatsbyImageData}
            alt=""
            objectFit="cover"
            className="!absolute w-full h-full -z-10"
          />
        )}
      </div>
    </div>
  );
};

// Padding has to be renamed: graphql gives an error
// when querying union types with identical field names but different types
export const query = graphql`
  fragment GridData on ContentfulGrid {
    title
    titleSize
    colorScheme
    titleAlignment
    itemsDensity
    _padding: padding
    backgroundImage {
      gatsbyImageData
    }
    items {
      __typename
      ... on ContentfulFlatCard {
        ...FlatCardData
      }
      ... on ContentfulImageCard {
        ...ImageCardData
      }
      ... on ContentfulMaterialCard {
        ...MaterialCardData
      }
      ... on ContentfulAppendixCard {
        ...AppendixCardData
      }
      ... on ContentfulPortraitCard {
        ...PortraitCardData
      }
      ... on ContentfulContactCard {
        ...ContactCardData
      }
      ... on ContentfulHorizontalCard {
        ...HorizontalCardData
      }
      ... on ContentfulCta {
        ...CtaData
      }
    }
  }
`;
